import { useState, useEffect } from 'react';
import { Bell, X, Smartphone } from '../../icons/wp-icons';
import {
  isPushSupported,
  isStandalonePWA,
  getPermissionStatus,
  requestPushPermission,
  subscribeToPush
} from '../../utils/pushNotifications';

export default function PushPermissionPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showPWAHint, setShowPWAHint] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if should show prompt
    const status = getPermissionStatus();
    setPermission(status);

    console.log('[PushPrompt] Permission status:', status);
    console.log('[PushPrompt] isPushSupported:', isPushSupported());
    console.log('[PushPrompt] isStandalone:', isStandalonePWA());

    // Check if dismissed recently
    const dismissed = localStorage.getItem('push-permission-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Check if PWA hint was dismissed
    const pwaHintDismissed = localStorage.getItem('pwa-hint-dismissed');
    const pwaHintDismissedTime = pwaHintDismissed ? parseInt(pwaHintDismissed) : 0;
    const daysSincePWAHintDismiss = (Date.now() - pwaHintDismissedTime) / (1000 * 60 * 60 * 24);

    // Detect iOS Safari (not standalone)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isIOSSafari = isIOS && isSafari;

    // Show PWA hint for iOS Safari users who haven't added to home screen
    if (isIOSSafari && !isStandalonePWA() && daysSincePWAHintDismiss > 3) {
      const timer = setTimeout(() => setShowPWAHint(true), 2000);
      return () => clearTimeout(timer);
    }

    // Show push prompt if:
    // - Default (not asked yet)
    // - Supported
    // - Not dismissed in last 7 days
    if (status === 'default' && isPushSupported() && daysSinceDismiss > 7) {
      // Show after 3 seconds
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAllow = async () => {
    try {
      setLoading(true);
      const perm = await requestPushPermission();
      setPermission(perm);

      if (perm === 'granted') {
        await subscribeToPush();
        setShowPrompt(false);
      } else {
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Push permission error:', error);
      alert('Fehler beim Aktivieren der Push Notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember dismissal
    localStorage.setItem('push-permission-dismissed', Date.now().toString());
  };

  const handleDismissPWAHint = () => {
    setShowPWAHint(false);
    localStorage.setItem('pwa-hint-dismissed', Date.now().toString());
  };

  // PWA Install Hint for iOS Safari
  if (showPWAHint) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up md:left-auto md:right-4 md:w-96">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-white" />
            </div>

            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">
                App installieren
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Für Push-Benachrichtigungen auf dem iPhone:
              </p>
              <ol className="text-sm text-gray-600 mb-3 list-decimal list-inside space-y-1">
                <li>Tippe auf das <strong>Teilen-Symbol</strong> (Quadrat mit Pfeil)</li>
                <li>Wähle <strong>"Zum Home-Bildschirm"</strong></li>
                <li>Öffne die App vom Home-Bildschirm</li>
              </ol>

              <button
                onClick={handleDismissPWAHint}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
              >
                Verstanden
              </button>
            </div>

            <button
              onClick={handleDismissPWAHint}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!showPrompt || permission !== 'default') {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up md:left-auto md:right-4 md:w-96">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-[#057867] rounded-lg flex items-center justify-center">
            <Bell className="w-6 h-6 text-white" />
          </div>

          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">
              Benachrichtigungen aktivieren
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Erhalten Sie Push-Benachrichtigungen für Termine, Anfragen und wichtige Updates
            </p>

            <div className="flex gap-2">
              <button
                onClick={handleAllow}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-[#057867] text-white rounded-lg hover:bg-[#045850] transition text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Aktiviere...' : 'Aktivieren'}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition text-sm"
              >
                Später
              </button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
